<template>
  <div class="c-search">
    <div v-if="!connection" class="c-search__unavailable">
      <div class="c-search__offline-wrapper">
        <offline-icon class="c-search__offline--icon"></offline-icon>

        <h3 class="c-search__heading c-search__heading--offline">
          {{ $t('SEARCH_OFFLINE_HEADING') }}
        </h3>

        <div class="c-search__content c-search__content--offline">
          {{ $t('SEARCH_OFFLINE') }}
        </div>
      </div>
    </div>
    <div v-else class="c-search__available">
      <div class="c-search-input__wrapper">
        <search-input @search-text="updateSearch" @search-text-clear="clearSearch"></search-input>

        <div v-if="searchText" class="c-search__results-info">
          {{ resultsInfo }}
        </div>
        <div v-else class="c-search__results-info"></div>
      </div>

      <div v-if="hasChapters && filteredSearch.length > 0" class="c-search__results-wrapper">
        <div v-for="(item, i) in filteredSearch" :key="i" class="c-search__results-item">
          <h3 class="c-search__heading">
            {{ item.title }}
          </h3>

          <router-link
            class="c-search__result c-search__result-heading"
            v-for="result in item.results"
            :key="result._id + result._score"
            :to="{ params: { page: getPageByIndex(result._source.position) } }"
            @click.native="highlightWord()"
          >
            <div class="c-search__result-sentence" v-html="'...' + result.highlight.text[0]"></div>

            <div class="c-search__result-page">
              {{ getPageByIndex(result._source.position) }}
            </div>
          </router-link>
        </div>
      </div>

      <div v-else-if="!hasChapters && results.hits.length > 0" class="c-search__results-wrapper">
        <div class="c-search__results-item">
          <h3 class="c-search__heading">
            {{ bookTitle }}
          </h3>

          <router-link
            class="c-search__result c-search__result-heading"
            v-for="result in results.hits"
            :key="result._id + result._score"
            :to="{ params: { page: getPageByIndex(result._source.position) } }"
            @click.native="highlightWord()"
            :aria-label="`${$t('GO_TO')} ${$t('SEARCH_RESULT')}`">
            <div class="c-search__result-sentence" v-html="'...' + result.highlight.text[0]"></div>

            <div class="c-search__result-page">
              {{ getPageByIndex(result._source.position) }}
            </div>
          </router-link>
        </div>
      </div>

      <div v-else-if="searchStatus === 'NO_RESULTS'" class="c-search__no-results-wrapper">
        <search-no-results></search-no-results>

        <h3 class="c-search__heading c-search__heading--no-results">
          {{ $t('SEARCH_NO_RESULTS') }}
        </h3>

        <div class="c-search__content c-search__content--no-results">
          {{ $t('SEARCH_NO_RESULTS_TEXT', { phrase: searchText }) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'

import bookService from '@/services/book'
import SearchInput from '@/components/SearchInput'
import SearchNoResults from '@/assets/svg/SearchNoResults'
import OfflineIcon from '@/assets/svg/OfflineIcon'
import { checkChapters, getPageByIndex } from '@/utils/utils'

export default {
  name: 'SearchInBook',

  components: {
    'search-input': SearchInput,
    'search-no-results': SearchNoResults,
    'offline-icon': OfflineIcon
  },

  data() {
    return {
      searchText: '',
      searchStatus: 'READY_TO_SEARCH',
      hasChapters: false,
      results: {
        hits: []
      }
    }
  },

  mounted() {
    this.hasChapters = !checkChapters(this.toc)

    document.querySelector('.c-search-input__input').focus()
  },

  updated() {
    this.hasChapters = !checkChapters(this.toc)
  },

  computed: {
    resultsInfo() {
      if (this.searchStatus === 'SEARCHING') {
        return this.$t('SEARCH_LOADING')
      }
      if (!this.results.total) {
        return ''
      }
      return this.results.total.value >= 0 ? `${this.results.total.value} ${this.$t('SEARCH_RESULT_COUNT')} “${this.searchText}”` : ''
    },

    filteredSearch() {
      let chaptersGroup = []
      const toc = this.toc
      const results = this.results.hits

      if (toc.length === 0) {
        return []
      }

      if (checkChapters(toc)) {
        return []
      }

      toc.forEach((t, n) => {
        let resultsArray = []
        const createPagesRange = () => {
          let pagesRange = []
          let length

          if (!t) return
          (n + 1) === toc.length ? length = this.pagesRange - t.pages[0].position : length = toc[(n + 1)].pages[0].position - t.pages[0].position

          for (let i = 0; i < length; i++) {
            pagesRange.push(t.pages[0].position + i)
          }

          return pagesRange
        }

        createPagesRange().forEach(tp => {
          results.filter(result => {
            if (result._source.position === tp) {
              resultsArray.push(result)
            }
          })
        })

        if (resultsArray.length > 0) {
          chaptersGroup.push({
            title: t.name,
            results: resultsArray
          })
        }
      })

      return chaptersGroup
    },

    ...mapState('book', {
      bookId: state => state.book._id,
      bookTitle: state => state.book.title,
      toc: state => state.toc,
      pagesRange: state=> state.book.content.length
    }),

    ...mapState('userSettings', {
      connection: state => state.connection
    })
  },

  methods: {
    getPageByIndex,
    updateSearch(val) {
      this.searchText = val
      this.searchStatus = 'SEARCHING'

      bookService.searchInBook(this.bookId, this.searchText).then(r => {
        this.results = r.hits

        if (this.results.hits.length === 0) {
          this.searchStatus = 'NO_RESULTS'

          return
        }

        this.searchStatus = 'READY_TO_SEARCH'
      })
    },

    highlightWord() {
      this.setHighlightSearchWord(this.searchText)
      this.closeModal()
    },

    clearSearch() {
      this.searchText = ''
      this.results = {
        hits: []
      }
      this.searchStatus = 'READY_TO_SEARCH'
    },

    ...mapActions({
      closeModal: 'bookMenu/closeModal',
      setHighlightSearchWord: 'bookMenu/setHighlightSearchWord'
    })
  }
}
</script>

<style lang="scss">
@import '@/assets/scss/_vars.scss';
@import '@/assets/scss/_mixin.scss';

.c-search__offline--icon {
  height: 9.75em;
  width: 7.68em;
}

.c-search-input__wrapper {
  max-width: 44em;
  width: 100%;
  margin: 0 auto;
}

.c-search__results-info {
  margin-top: 0.875em;
  font-size: 0.75em;
  color: $dark-blue;
  height: 0.75em;
}

.c-search__results-wrapper {
  margin-top: 2.375em;
}

.c-search__no-results-wrapper {
  text-align: center;
  margin-top: 4em;
}

.c-search__offline-wrapper {
  text-align: center;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  padding-right: 3.75em;
  width: 100%;
}

.c-search__heading {
  font-size: 1.125em;
  color: $dark-blue;
  font-weight: 700;
}

.c-search__heading--no-results {
  margin-top: 3em;
  margin-bottom: 1.25em;
}

.c-search__content {
  color: $black;
  font-size: 0.875em;
  max-width: 22em;
  margin: 0 auto;
}

.c-search__content--offline  {
  margin-top: 1.25em;
}

.c-search__results-item {
  margin-bottom: 2em;
}

.c-search__result {
  padding-top: 1.2em;
  border-bottom: 0.1em solid rgba($dark-blue-grey, 0.1);
  padding-bottom: 1.2em;
  position: relative;

  @include a11yOutline();

  &:hover {
    &:after {
      content: '';
      position: absolute;
      left: -2em;
      right: -2em;
      bottom: 0;
      top: 0;
      background: rgba($light-grey-blue, 0.2);
      border-radius: 3em;
    }
  }
}

.c-search__result-heading {
  display: flex;
  color: $dark-blue;
}

.c-search__result-sentence {
  color: $black;
}

.c-search__result-page {
  margin-left: auto;
  font-weight: 700;
}
</style>
